import { useState } from "react";
import { X, Mail, Lock, User, Loader2, LogIn } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

export default function AuthModal({ isOpen, onClose, initialMode }) {
  var auth = useAuth();
  var [mode, setMode] = useState(initialMode || "login");
  var [isSubmitting, setIsSubmitting] = useState(false);
  var [formData, setFormData] = useState({ name: "", email: "", password: "" });

  function handleChange(e) {
    setFormData(function(prev) { return { ...prev, [e.target.name]: e.target.value }; });
  }

  function handleSubmit(e) {
    e.preventDefault();
    setIsSubmitting(true);
    var promise = mode === "login" 
      ? auth.login(formData.email, formData.password)
      : auth.register(formData.name, formData.email, formData.password);
    
    promise
      .then(function() {
        toast.success(mode === "login" ? "Welcome back!" : "Account created!");
        onClose();
      })
      .catch(function(err) {
        toast.error(err.response?.data?.detail || "Authentication failed");
      })
      .finally(function() { setIsSubmitting(false); });
  }

  function handleGoogleLogin() {
    auth.loginWithGoogle();
  }

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black/80 z-[60]" onClick={onClose} />
      <div className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-md bg-[#0a0a0a] border border-white/10 rounded-sm z-[60]" data-testid="auth-modal">
        <div className="p-6 border-b border-white/10 flex items-center justify-between">
          <h2 className="font-heading font-bold text-white text-xl uppercase">{mode === "login" ? "Sign In" : "Create Account"}</h2>
          <button onClick={onClose} className="text-[#A1A1AA] hover:text-white"><X className="w-6 h-6" /></button>
        </div>

        <div className="p-6">
          <Button onClick={handleGoogleLogin} className="w-full h-12 bg-white hover:bg-gray-100 text-black font-semibold mb-6" data-testid="google-login-btn">
            <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24"><path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
            Continue with Google
          </Button>

          <div className="flex items-center gap-4 mb-6">
            <div className="flex-1 h-px bg-white/10" />
            <span className="text-[#71717a] text-sm">or</span>
            <div className="flex-1 h-px bg-white/10" />
          </div>

          <form onSubmit={handleSubmit} className="space-y-4" data-testid="auth-form">
            {mode === "register" && (
              <div className="space-y-2">
                <Label className="text-[#A1A1AA] text-xs uppercase">Name</Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#71717a]" />
                  <Input name="name" value={formData.name} onChange={handleChange} required className="input-tactical pl-10" placeholder="Your name" data-testid="auth-name" />
                </div>
              </div>
            )}
            <div className="space-y-2">
              <Label className="text-[#A1A1AA] text-xs uppercase">Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#71717a]" />
                <Input name="email" type="email" value={formData.email} onChange={handleChange} required className="input-tactical pl-10" placeholder="email@example.com" data-testid="auth-email" />
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-[#A1A1AA] text-xs uppercase">Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#71717a]" />
                <Input name="password" type="password" value={formData.password} onChange={handleChange} required className="input-tactical pl-10" placeholder="••••••••" data-testid="auth-password" />
              </div>
            </div>
            <Button type="submit" className="btn-primary w-full" disabled={isSubmitting} data-testid="auth-submit">
              {isSubmitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <LogIn className="w-4 h-4 mr-2" />}
              {mode === "login" ? "Sign In" : "Create Account"}
            </Button>
          </form>

          <p className="text-center text-[#A1A1AA] text-sm mt-6">
            {mode === "login" ? "Don't have an account? " : "Already have an account? "}
            <button onClick={function() { setMode(mode === "login" ? "register" : "login"); }} className="text-[#C0A062] hover:underline" data-testid="toggle-auth-mode">
              {mode === "login" ? "Sign up" : "Sign in"}
            </button>
          </p>
        </div>
      </div>
    </>
  );
}
